import * as React from "react";
import { useIntl } from "react-intl";
import { Dropdown, Grid, Menu } from "semantic-ui-react";

import { useCategories, useLocalStorage } from "@/utils";
import { usePlayerDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";

import { PromptServices } from "@/services";

import PromptsList from "./components/PromptList";

import "./styles.scss";
import { descriptors, LibraryType } from "./descriptors";
import { fromPrompt, SharedPostFlowDefaults } from "@/components/PostDialogs/SharedPostFlowDefaults";
import { PlayerPostDialog } from "@/components/PostDialogs/PlayerPostDialog";
import { SearchComponent } from "@/components/Search";
import { useMemo } from "react";
import { PromptCategoryCards } from "@/components/PromptCategoryCards";
import { PlayerPrompt, Prompt, SearchFilter } from "@/interfaces";
import { PromptKindFilter } from "@/components/PromptKindFilter";
import { filterByKind, isDynamicPrompt, PromptKind } from "@/containers/Coach/Library/PromptsTab/promptKind";
import { useShareFlow } from "@/components/ShareDesignPost/ShareFlowProvider";
import { PlayerIntroCard } from "@/components/ShareDesignPost/PlayerIntroCard";
import { toast } from "sonner";

const EMPTY_KIND_MESSAGE: Record<Exclude<PromptKind, "ALL">, string> = {
  DYNAMIC: "No personalized prompts yet.",
  STATIC: "No prompts match.",
};

interface OwnProps {
  coachId: string | number;
  playerId: number;
  reloadPrompts?: boolean;
}

type Props = OwnProps;

const PlayerLibraryListContentFC: React.FC<Props> = ({ reloadPrompts = false, playerId }) => {
  const { formatMessage } = useIntl();
  const [promptList, setPromptList] = React.useState<PlayerPrompt[]>([]);
  const [viewType, setViewType] = React.useState<"LIST" | "CARD">(
    localStorage.getItem("libraryViewType") == "CARD" ? "CARD" : "LIST"
  );

  const [showPostModal, setShowPostModal] = React.useState<boolean>(false);
  const [selectedPrompt, setSelectedPrompt] = React.useState<Prompt | undefined>(undefined);

  const { categories, categoryOptions } = useCategories("LIBRARY");
  const { open: openShareFlow } = useShareFlow();

  const canSeeDynamic = usePlayerDynamicPromptsAccess(playerId) === true;
  const mode = canSeeDynamic ? "dyn" : "static";
  // The list is refetched when the setting resolves; a late response from the other mode is dropped.
  const modeRef = React.useRef(mode);
  modeRef.current = mode;
  const extraFilters: Partial<SearchFilter> = React.useMemo(
    () => ({ deliveryModes: canSeeDynamic ? ["LIBRARY", "DYNAMIC"] : undefined }),
    [canSeeDynamic]
  );
  const refreshData = React.useCallback(
    (list: PlayerPrompt[]) => {
      if (modeRef.current === mode) setPromptList(list);
    },
    [mode]
  );

  const [storedPromptKind, setPromptKind] = useLocalStorage<PromptKind>("libraryPromptKind", "ALL");
  // Without dynamic prompts the filter isn't shown, and the stored choice waits for when it is.
  const promptKind: PromptKind = canSeeDynamic ? storedPromptKind : "ALL";
  const visiblePrompts = useMemo(() => {
    const kept = new Set(
      filterByKind(
        promptList.map(p => p.prompt),
        promptKind
      )
    );
    return promptList.filter(p => kept.has(p.prompt));
  }, [promptList, promptKind]);
  const emptyMessage =
    visiblePrompts.length === 0 && promptList.length > 0 && promptKind !== "ALL"
      ? EMPTY_KIND_MESSAGE[promptKind]
      : undefined;

  const openDynamicPrompt = React.useCallback(
    (prompt: Prompt) => {
      if (!prompt.designPackageId) {
        toast.error("This design isn't available right now.");
        return;
      }
      openShareFlow({ kind: "library", promptId: Number(prompt.promptId), designPackageId: prompt.designPackageId });
    },
    [openShareFlow]
  );
  const hasDynamicPrompt = useMemo(() => promptList.some(p => isDynamicPrompt(p.prompt)), [promptList]);

  const content = useMemo(() => {
    if (viewType == "LIST") {
      return (
        <PromptsList
          categories={categories}
          promptList={visiblePrompts}
          emptyMessage={emptyMessage}
          onOpenDynamicPrompt={openDynamicPrompt}
        />
      );
    } else {
      return (
        <PromptCategoryCards
          categories={categories}
          promptList={visiblePrompts.map(p => p.prompt)}
          onClickPrompt={prompt => {
            if (isDynamicPrompt(prompt)) {
              openDynamicPrompt(prompt);
              return;
            }
            setSelectedPrompt(prompt);
            setShowPostModal(true);
          }}
        />
      );
    }
  }, [viewType, categories, visiblePrompts, emptyMessage, openDynamicPrompt, setSelectedPrompt, setShowPostModal]);

  const postDefaults: SharedPostFlowDefaults | undefined = React.useMemo(() => {
    return selectedPrompt ? fromPrompt(selectedPrompt) : undefined;
  }, [selectedPrompt]);

  return (
    <Grid data-elm-id={"playerLibraryListContent"}>
      <Grid.Row>
        <Grid.Column>
          {hasDynamicPrompt && <PlayerIntroCard />}
          <SearchComponent
            key={mode}
            cacheKey={`playerPrompts:${mode}`}
            refreshData={refreshData}
            extraFilters={extraFilters}
            forceRefresh={reloadPrompts}
            content={content}
            customCoachId={`${playerId}`}
            filterPlaceholder={formatMessage({ ...descriptors[LibraryType.byCategory] })}
            additionalFilterContent={
              <>
                {canSeeDynamic && (
                  <Menu.Item>
                    <PromptKindFilter value={promptKind} onChange={setPromptKind} />
                  </Menu.Item>
                )}
                <Menu.Item>
                  <Dropdown
                    selection
                    placeholder={"View Type"}
                    className="searchInput"
                    selectOnBlur={false}
                    onChange={(_, data) => {
                      localStorage.setItem("libraryViewType", data.value as "LIST" | "CARD");
                      setViewType(data.value as "LIST" | "CARD");
                    }}
                    value={viewType}
                    options={[
                      { text: "List View", value: "LIST", icon: "list" },
                      { text: "Card View", value: "CARD", icon: "block layout" },
                    ]}
                  />
                </Menu.Item>
              </>
            }
            searchFunction={PromptServices.playerLibrary}
            showSortBy={false}
            filters={categoryOptions}
            pageable={false}
          />
          {showPostModal && postDefaults && (
            <PlayerPostDialog
              open={showPostModal}
              defaults={postDefaults}
              handleClose={() => {
                setShowPostModal(false);
                setSelectedPrompt(undefined);
              }}
            />
          )}
        </Grid.Column>
      </Grid.Row>
    </Grid>
  );
};

export const PlayerPrompts = PlayerLibraryListContentFC;
